import { useEffect, useRef, useState } from "react";

import { className } from "utils/strings";
import useWindowDimensions from "hooks/useWindowDimension";
import { INiche } from "services/types";
import { routes } from "routes";

import { LessTagsButton } from "./LessTagsButton";
import { MoreTagsButton } from "./MoreTagsButton";
import { TagListButton } from "./TagListButton";
import { ITag, ITagListItem } from "./types";

import "./TagList.scss";

const MORE_WIDTH = 37;
const TAG_GAP = 12;

interface IWidths {
  [tag: string]: number;
}

export const TagList = (props: {
  tags: ITag[];
  secondRow?: boolean;
  changeTags?: (name: string) => void;
  selectedTags?: string[];
  niches?: INiche[];
}): JSX.Element => {
  const ref = useRef<HTMLDivElement>(null);
  const [widths, setWidths] = useState<IWidths>({});
  const [parentWidth, setParentWidth] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [hiddenItems, setHiddenItems] = useState<string[]>([]);
  const [expanded, setExpanded] = useState<boolean>(false);
  const isDesktop = useWindowDimensions();

  // Combined niches + tags.
  const [listItems, setListItems] = useState<ITagListItem[]>([]);

  useEffect(() => {
    const items = [];

    if (props.niches) {
      for (const niche of props.niches) {
        items.push({
          type: "niche",
          id: `niche:${niche.id}`,
          text: niche.name,
          link: routes.niche(niche.id),
          thumbnail: niche.thumbnail ? niche.thumbnail : ""
        });
      }
    }

    for (const tag of props.tags) {
      items.push({
        type: "tag",
        id: `tag:${tag.name}`,
        text: tag.name,
        link: routes.singleTag(tag.name),
      });
    }

    setListItems(items);
  }, [props.tags, props.niches]);

  useEffect(() => {
    if (ref.current && ref.current.clientWidth) {
      setParentWidth(ref.current.clientWidth);
    }
  }, [ref]);

  useEffect(() => {
    if (parentWidth > 0 && Object.keys(widths).length > 0) {
      const allowedWidth = parentWidth - MORE_WIDTH;
      const hidden = [];
      let currentWidth = 0;

      for (const item of listItems) {
        const tagWidth = (widths[item.id] ?? 0) + TAG_GAP;
        const maxAllowedWidth = props?.secondRow ? allowedWidth * (isDesktop ? 2 : 3) : allowedWidth;

        if ((currentWidth + tagWidth < maxAllowedWidth) && hidden.length === 0) {
          currentWidth += tagWidth;
        } else {
          hidden.push(item.id);
        }
      }

      setHiddenItems(hidden);
      setHasMore(hidden.length > 0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listItems, parentWidth, widths]);

  // Initially all tags are .visible; after we update the classes
  // to hide the tags that don't fit, this gets called again,
  // with zero widths for each extra tag.  So we ignore zero widths.
  const onWidth = (tag: string, width: number): void => {
    // Prevent looping updates.  Width never changes.
    const last = widths[tag] ?? 0;

    if (width > 0 && width !== last) {
      setWidths((prev: IWidths) => ({ ...prev, [tag]: width }));
    }
  };

  const isItemVisible = (id: string): boolean => {
    return hiddenItems.indexOf(id) < 0;
  };

  const onShowMore = () => {
    setExpanded(true);
  };

  const onShowLess = () => {
    setExpanded(false);
  };

  return (
    <div className={className({
      "tagList": true,
      "expanded": expanded,
      "collapsed": !expanded,
      "hasmore": hasMore,
      "nomore": !hasMore,
    })} ref={ref}>
      {listItems.map((item: ITagListItem, index: number) => (
        <TagListButton
          key={item.id}
          item={item}
          visible={isItemVisible(item.id)}
          onWidth={onWidth}
          onTagClick={props.changeTags}
          selectedTags={props.selectedTags}
        />
      ))}
      <MoreTagsButton
        onClick={onShowMore}
      />
      <LessTagsButton
        onClick={onShowLess}
      />
    </div>
  );
};
